import { toast } from "sonner";
import {
  useApproveEscalationDraftMutation,
  useRejectEscalationDraftMutation,
} from "../api/queries/escalationQueries";
import { useAuth } from "../hooks/useAuth";

type DraftWithApproval = {
  uid: string;
  to?: string[];
  requires_approval?: boolean;
  approval_status?: string | null;
  escalation_level_name?: string | null;
};

export function EscalationApprovalBanner({
  draft,
  invoiceId,
  onUpdated,
}: {
  draft: DraftWithApproval;
  invoiceId: string;
  onUpdated?: () => void;
}) {
  const { isOrgManager } = useAuth();
  const approve = useApproveEscalationDraftMutation();
  const reject = useRejectEscalationDraftMutation();

  if (!draft.requires_approval || draft.approval_status !== "pending") {
    return null;
  }

  const recipients = (draft.to ?? []).join(", ");

  return (
    <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
      <p className="font-medium">External escalation email ready for review.</p>
      <p className="mt-1 text-amber-800">
        To: {recipients || "—"}
        {draft.escalation_level_name
          ? ` · Step: ${draft.escalation_level_name}`
          : ""}
      </p>
      {isOrgManager ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded bg-amber-600 px-3 py-1.5 text-white hover:bg-amber-700"
            disabled={approve.isPending}
            onClick={async () => {
              try {
                await approve.mutateAsync(draft.uid);
                toast.success("Draft approved — you can send it now");
                onUpdated?.();
              } catch {
                toast.error("Failed to approve draft");
              }
            }}
          >
            Approve
          </button>
          <button
            type="button"
            className="rounded border border-amber-400 px-3 py-1.5 hover:bg-amber-100"
            disabled={reject.isPending}
            onClick={async () => {
              try {
                await reject.mutateAsync(draft.uid);
                toast.success("Draft rejected");
                onUpdated?.();
              } catch {
                toast.error("Failed to reject draft");
              }
            }}
          >
            Reject
          </button>
        </div>
      ) : (
        <p className="mt-2 text-xs text-amber-700">Pending manager approval</p>
      )}
    </div>
  );
}
